/**
 * 構成 — the article's outline, beside the article.
 *
 * After the plan screen the structure used to vanish: the editor showed one
 * Markdown blob, and changing the shape meant going back two screens and
 * regenerating everything. This is the plan made permanent - what the article is
 * made of, how long each part is against what it was aiming for, and the two
 * actions that matter on a section: go there, and write it again.
 *
 * IT HAS TWO STATES, AND SAYS WHICH ONE IT IS IN
 *
 *   IN STEP - every planned heading is in the text, in order. Jumping and
 *   redrawing are offered, because the offsets are known to be right.
 *
 *   TAKEN OVER - somebody renamed a heading, added one, or removed one. The rail
 *   becomes a read-only map of the document's own headings and says so. It does
 *   NOT guess which section a renamed heading used to be: guessing is how a
 *   redraw overwrites a paragraph somebody wrote by hand.
 *
 * See outline.ts for the matching, which is strict on purpose.
 */
import { useState } from 'react';
import { Card, Empty, Pill } from './ui.tsx';
import { Textarea } from './Textarea.tsx';
import type { ArticleSection } from '../api.ts';
import { locate, parseHeadings, sectionLength } from '../outline.ts';
import { visibleLength } from '../../../shared/rich-format.ts';

/** How far off target a section may be before the rail says so. */
const TOLERANCE = 0.3;

export function OutlineRail({
  sections,
  body,
  busy,
  onJump,
  onRedraw,
}: {
  sections: ArticleSection[];
  body: string;
  /** The ordinal being redrawn, or null. */
  busy: number | null;
  /** By the heading's words: the document has no Markdown offsets. */
  onJump: (heading: string) => void;
  onRedraw: (ordinal: number, instruction: string) => void;
}) {
  const [openNote, setOpenNote] = useState<number | null>(null);
  const [note, setNote] = useState('');

  const ranges = locate(
    body,
    sections.map((section) => section.heading),
  );

  if (sections.length === 0) {
    return (
      <Card title="構成" caption="この記事には構成が登録されていません。">
        <Empty>
          構成を作ると、節ごとの文字数や書き直しがここから使えます。
        </Empty>
      </Card>
    );
  }

  /*
   * The plan and the text have parted company. Show what the document actually
   * contains - which is still useful for navigating - and be plain about why the
   * per-section actions are gone.
   */
  if (!ranges) {
    const found = parseHeadings(body);
    return (
      <Card
        title="構成"
        caption="本文の見出しが構成と一致しません。本文の見出しをそのまま表示しています。"
        actions={<Pill tone="warn">手動で編集済み</Pill>}
      >
        {found.length === 0 ? (
          <Empty>本文に見出しがありません。</Empty>
        ) : (
          <ol className="outline">
            {found.map((heading) => (
              <li key={`${heading.start}`} className="outline__row">
                <button
                  type="button"
                  className="outline__jump"
                  onClick={() => onJump(heading.text)}
                >
                  <span className={`outline__heading outline__heading--h${heading.level}`}>
                    {heading.text}
                  </span>
                  <span className="muted small">
                    {body.slice(heading.start, heading.end).trim().length.toLocaleString('ja-JP')}
                    文字
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
        <p className="muted small" style={{ marginBottom: 0 }}>
          節ごとの書き直しは、見出しが構成と一致しているときだけ使えます。構成を作り直すか、
          見出しを元の文言に戻すと再び使えます。
        </p>
      </Card>
    );
  }

  /*
   * The whole article, counted the way every other screen counts it.
   *
   * `body.trim().length` counted the stored markup: an article with eight tables
   * reported 9,224 characters against a 4,700 target here and 4,431 on the
   * writing and final screens, because nearly six thousand of it was
   * `<table style="min-width: 100px;">…`. One definition, in shared/rich-format.
   */
  const total = visibleLength(body);
  const targetTotal = sections.reduce((sum, section) => sum + (section.target_chars ?? 0), 0);

  return (
    <Card
      title="構成"
      caption="節を選ぶと本文の該当箇所に移動します。書き直しはその節だけに反映されます。"
      actions={
        <span className="muted small">
          {total.toLocaleString('ja-JP')}
          {targetTotal > 0 && ` / 目安 ${targetTotal.toLocaleString('ja-JP')}`}文字
        </span>
      }
    >
      <ol className="outline">
        {sections.map((section, at) => {
          const range = ranges[at]!;
          const chars = sectionLength(body, range);
          const target = section.target_chars;
          const off = target ? Math.abs(chars - target) / target : 0;
          const working = busy === section.ordinal;

          return (
            <li key={section.id} className="outline__row">
              <button
                type="button"
                className="outline__jump"
                onClick={() => onJump(section.heading)}
              >
                <span className={`outline__heading outline__heading--h${section.level}`}>
                  {section.heading}
                </span>
                <span className="muted small">
                  {chars.toLocaleString('ja-JP')}
                  {target ? ` / ${target.toLocaleString('ja-JP')}` : ''}文字
                  {/*
                    Said in words as well as by position: a section at half its
                    target is the defect section drafting exists to remove, and it
                    should be visible without counting.
                  */}
                  {target && off > TOLERANCE && (
                    <>
                      {'　'}
                      <Pill tone="warn">{chars < target ? '短い' : '長い'}</Pill>
                    </>
                  )}
                </span>
              </button>

              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy !== null}
                onClick={() => setOpenNote(openNote === section.ordinal ? null : section.ordinal)}
              >
                {working ? '書き直し中…' : 'この節を書き直す'}
              </button>

              {openNote === section.ordinal && (
                <div className="outline__note">
                  {section.intent && (
                    <p className="muted small" style={{ margin: '0 0 6px' }}>
                      狙い: {section.intent}
                    </p>
                  )}
                  <Textarea
                    id={`redraw-${section.ordinal}`}
                    minRows={2}
                    maxLength={300}
                    value={note}
                    placeholder="指示（任意）例：費用の内訳を表にして、所要時間も入れる"
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <div className="chips" style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy !== null}
                      onClick={() => {
                        const instruction = note;
                        setOpenNote(null);
                        setNote('');
                        onRedraw(section.ordinal, instruction);
                      }}
                    >
                      この節だけ書き直す
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setOpenNote(null)}
                    >
                      やめる
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <p className="muted small" style={{ marginBottom: 0 }}>
        書き直した内容は本文に差し込まれます。保存するまで記事は変わりません。
      </p>
    </Card>
  );
}
